import React, { useState, useEffect } from "react";

import { useNavigate, useParams, useLocation } from "react-router-dom";

import FollowButton from "../components/FollowButton";
import HeadToHead from "../components/HeadToHead";
import LeftMenu from "../components/LeftMenu";
import MatchStats from "../components/MatchStats";
import MobileMenu from "../components/MobileMenu";
import PlyrVideoPlayer from "../components/PlyrVideoPlayer";
import apiService from "../services/api";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import { trackGameView } from "../utils/sportsPreferences";
import webPlayerStorage from "../utils/webPlayerStorage";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SPORT_HERO_BACKGROUNDS = {
  nba: "/sports-bg-basketball.png",
  basketball: "/sports-bg-basketball.png",
  nfl: "/sports-bg-nfl.png",
  ncaaf: "/sports-bg-nfl.png",
  "american football": "/sports-bg-nfl.png",
  nhl: "/sports-bg-nhl.png",
  "ice hockey": "/sports-bg-nhl.png",
  hockey: "/sports-bg-nhl.png",
  mlb: "/sports-bg-mlb.png",
  baseball: "/sports-bg-mlb.png",
  soccer: "/sports-bg-soccer.png",
  football: "/sports-bg-soccer.png",
  mls: "/sports-bg-soccer.png",
  "premier league": "/sports-bg-soccer.png",
  default: "/sports-bg-basketball.png",
};

const TABS = [
  { key: "overview", label: "Overview", icon: "fa-th-large" },
  { key: "channels", label: "Channels", icon: "fa-satellite-dish" },
  { key: "stats", label: "Stats", icon: "fa-chart-bar" },
  { key: "timeline", label: "Timeline", icon: "fa-stream" },
  { key: "h2h", label: "H2H", icon: "fa-exchange-alt" },
];

// ---------------------------------------------------------------------------
// Helpers (outside component)
// ---------------------------------------------------------------------------

const getYouTubeId = (url) => {
  if (!url) return null;
  if (url.includes("youtube.com/watch?v=")) {
    const id = url.split("v=")[1];

    return id ? id.split("&")[0] : null;
  }
  if (url.includes("youtu.be/")) {
    const id = url.split("youtu.be/")[1];

    return id ? id.split("?")[0] : null;
  }

  return null;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WebPlayerSportsTestEventDetail = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const location = useLocation();

  // Core state
  const event = location.state?.event || null;

  // Tab state
  const [activeTab, setActiveTab] = useState("overview");
  const [focusedTab, setFocusedTab] = useState(0);

  // Player state
  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentStreamUrl, setCurrentStreamUrl] = useState(null);
  const [currentStreamTitle, setCurrentStreamTitle] = useState(null);

  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [highlights, setHighlights] = useState([]);

  const profile = webPlayerStorage.getProfile();
  const playlist = webPlayerStorage.getPlaylist();

  // -----------------------------------------------------------------------
  // Derived event data
  // -----------------------------------------------------------------------

  const homeTeam = event?.strHomeTeam || event?.homeTeam;
  const awayTeam = event?.strAwayTeam || event?.awayTeam;

  const getNumericScore = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "string") return parseInt(value, 10) || 0;
    if (typeof value === "object") return 0;

    return 0;
  };

  const homeScore =
    getNumericScore(event?.intHomeScore) ||
    getNumericScore(event?.goals?.home) ||
    getNumericScore(event?.homeTeamScore) ||
    0;
  const awayScore =
    getNumericScore(event?.intAwayScore) ||
    getNumericScore(event?.goals?.away) ||
    getNumericScore(event?.awayTeamScore) ||
    0;

  const homeTeamLogo = event?.strHomeTeamBadge || event?.homeTeamLogo;
  const awayTeamLogo = event?.strAwayTeamBadge || event?.awayTeamLogo;
  const league = event?.strLeague || event?.league;
  const leagueLogo = event?.strLeagueBadge || event?.leagueLogo;
  const sportName = event?.strSport || event?.genre || "SPORTS";
  const venue =
    event?.strVenue ||
    (typeof event?.venue === "string" ? event.venue : event?.venue?.name) ||
    "Madison Square Garden, New York";
  const season = event?.strSeason || event?.season;
  const referee = event?.strReferee || event?.referee;
  const attendance = event?.intSpectators || event?.attendance;

  const gameStatus =
    event?.status?.short || event?.strStatus || event?.period || "";
  const gameTime = event?.status?.timer || event?.strTime || "";
  const gameDate =
    event?.date || event?.dateEvent || event?.strTimestamp || event?.timestamp;

  const eventRating = event?.rating?.toUpperCase() || "";
  const isLive =
    eventRating === "LIVE" ||
    (gameStatus &&
      (gameStatus.toLowerCase().includes("live") ||
        [
          "1",
          "2",
          "3",
          "4",
          "Q1",
          "Q2",
          "Q3",
          "Q4",
          "P1",
          "P2",
          "P3",
          "OT",
        ].includes(gameStatus)));

  const isFinal =
    gameStatus &&
    (gameStatus.toLowerCase().includes("final") ||
      ["FT", "AOT", "AET"].includes(gameStatus));

  // Sport background
  const sportKey =
    event?.sport?.toLowerCase() ||
    event?.strSport?.toLowerCase() ||
    event?.genre?.toLowerCase() ||
    "default";
  const sportBg =
    SPORT_HERO_BACKGROUNDS[sportKey] || SPORT_HERO_BACKGROUNDS["default"];

  // -----------------------------------------------------------------------
  // Format helpers
  // -----------------------------------------------------------------------

  const formatGameDate = () => {
    if (!gameDate) return "";
    try {
      const date = new Date(gameDate);

      return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return gameDate;
    }
  };

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleChannelClick = (channel) => {
    let streamUrl = channel.stream_url;
    const channelName = channel.name;

    if (!streamUrl) return;
    try {
      const decodedUrl = atob(streamUrl);

      if (
        decodedUrl.startsWith("http://") ||
        decodedUrl.startsWith("https://")
      ) {
        streamUrl = decodedUrl;
      }
    } catch (e) {
      /* Not base64, use as-is */
    }
    setCurrentStreamUrl(streamUrl);
    setCurrentStreamTitle(channelName);
    setPlayerOpen(true);
  };

  const handleClosePlayer = () => {
    setPlayerOpen(false);
    setCurrentStreamUrl(null);
    setCurrentStreamTitle(null);
  };

  // -----------------------------------------------------------------------
  // Data fetchers
  // -----------------------------------------------------------------------

  const fetchChannels = async () => {
    try {
      setChannelsLoading(true);
      const mediaId = event?.idEvent || event?.id || eventId;

      if (!mediaId) {
        setChannels([]);
        setChannelsLoading(false);

        return;
      }
      const sport = event?.sport;
      const endpoint = sport
        ? `/v2/sports/events/live/${mediaId}?sport=${sport.toLowerCase()}`
        : `/v2/sports/events/live/${mediaId}`;
      const response = await apiService.webPlayerRequest(endpoint);
      const liveTvChannels = response?.data?.liveTv || response?.liveTv || [];
      const mappedChannels = liveTvChannels.map((channel) => {
        const currentProgram = channel.programmes?.[0];

        return {
          ...channel,
          stream_url: channel.streamUrl,
          stream_icon: channel.logoPath || channel.posterPath,
          epg_channel_id: channel.epgChannelId,
          currentProgram: currentProgram
            ? {
                title: currentProgram.title,
                description: currentProgram.description,
                start: new Date(currentProgram.start).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                stop: new Date(currentProgram.stop).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              }
            : null,
        };
      });

      setChannels(mappedChannels);
      setChannelsLoading(false);
    } catch (error) {
      console.error("Error finding channels:", error);
      setChannels([]);
      setChannelsLoading(false);
    }
  };

  const fetchTimeline = async () => {
    try {
      setTimelineLoading(true);
      if (event?.liveEvents?.length > 0) {
        setTimeline(event.liveEvents);
        setTimelineLoading(false);

        return;
      }
      const mediaId = event?.idEvent || event?.id || eventId;

      if (!mediaId) {
        setTimeline([]);
        setTimelineLoading(false);

        return;
      }
      const sport = event?.sport;
      const endpoint = sport
        ? `/v2/sports/events/live/${mediaId}?sport=${sport.toLowerCase()}`
        : `/v2/sports/events/live/${mediaId}`;
      const response = await apiService.webPlayerRequest(endpoint);
      const events =
        response?.data?.events ||
        response?.events ||
        response?.data?.liveEvents ||
        response?.liveEvents ||
        [];

      setTimeline(events);
      setTimelineLoading(false);
    } catch (error) {
      console.error("Error fetching timeline:", error);
      setTimeline([]);
      setTimelineLoading(false);
    }
  };

  const fetchHighlights = async () => {
    try {
      if (!homeTeam || !awayTeam) return;
      const searchQuery = encodeURIComponent(`${homeTeam}_vs_${awayTeam}`);
      const response = await apiService.webPlayerRequest(
        `/v2/sports/highlights?q=${searchQuery}`
      );
      const events =
        response?.data?.event || response?.event || response?.data || [];
      const withVideo = (Array.isArray(events) ? events : [])
        .filter((e) => e.strVideo)
        .slice(0, 5)
        .map((e) => ({
          id: e.idEvent,
          title: e.strEvent || `${homeTeam} vs ${awayTeam}`,
          video: e.strVideo,
          thumb: e.strThumb || e.strPoster,
          date: e.dateEvent,
          league: e.strLeague,
        }));

      setHighlights(withVideo);
    } catch (error) {
      setHighlights([]);
    }
  };

  // -----------------------------------------------------------------------
  // Effects
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (event) {
      trackGameView(event);
      fetchChannels();
      fetchTimeline();
      fetchHighlights();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // DPAD navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case "Escape":
        case "Backspace":
          e.preventDefault();
          navigate(-1);
          break;
        case "ArrowLeft":
          e.preventDefault();
          setFocusedTab((prev) => {
            const next = Math.max(0, prev - 1);

            setActiveTab(TABS[next].key);

            return next;
          });
          break;
        case "ArrowRight":
          e.preventDefault();
          setFocusedTab((prev) => {
            const next = Math.min(TABS.length - 1, prev + 1);

            setActiveTab(TABS[next].key);

            return next;
          });
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // -----------------------------------------------------------------------
  // Momentum computation (for Watch tab)
  // -----------------------------------------------------------------------

  const computeMomentum = () => {
    if (!timeline || timeline.length === 0) return { home: 50, away: 50 };
    let homePoints = 0;
    let awayPoints = 0;

    timeline.forEach((evt) => {
      const isHome =
        evt.team?.name === homeTeam || evt.team?.name === "HOME" || false;
      const points =
        evt.type === "Goal"
          ? 3
          : evt.type === "Card"
            ? 1
            : evt.type === "subst"
              ? 0.5
              : 0.5;

      if (isHome) homePoints += points;
      else awayPoints += points;
    });
    const total = homePoints + awayPoints || 1;

    return {
      home: Math.round((homePoints / total) * 100),
      away: Math.round((awayPoints / total) * 100),
    };
  };

  // -----------------------------------------------------------------------
  // Timeline event icon helper
  // -----------------------------------------------------------------------

  const getTimelineIcon = (evt) => {
    const type = (evt.type || "").toLowerCase();
    const detail = (evt.detail || "").toLowerCase();

    if (type === "goal" || detail.includes("goal")) {
      return (
        <div className="w-8 h-8 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center">
          <i className="fas fa-futbol text-green-400 text-sm" />
        </div>
      );
    }
    if (type === "card" && detail.includes("yellow")) {
      return (
        <div className="w-8 h-8 rounded-full bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center">
          <div className="w-3 h-4 rounded-sm bg-yellow-400" />
        </div>
      );
    }
    if (type === "card" && detail.includes("red")) {
      return (
        <div className="w-8 h-8 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
          <div className="w-3 h-4 rounded-sm bg-red-500" />
        </div>
      );
    }
    if (type === "subst" || detail.includes("substitution")) {
      return (
        <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center">
          <i className="fas fa-exchange-alt text-blue-400 text-xs" />
        </div>
      );
    }
    if (type === "var" || detail.includes("var")) {
      return (
        <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
          <i className="fas fa-desktop text-purple-400 text-xs" />
        </div>
      );
    }

    return (
      <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
        <i className="fas fa-circle text-white/30 text-[6px]" />
      </div>
    );
  };

  // -----------------------------------------------------------------------
  // Render: no event
  // -----------------------------------------------------------------------

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LeftMenu activeMenuItem="sportstest" />
        <div className="text-center">
          <i className="fas fa-football-ball text-4xl text-white/20 mb-4" />
          <p className="text-white/40 text-lg">Event not found</p>
          <button
            className="mt-4 px-6 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 transition-colors"
            onClick={() => navigate("/web-player/sports-test")}
          >
            Back to Sports
          </button>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: main page
  // -----------------------------------------------------------------------

  const momentum = computeMomentum();
  const topChannels = channels.slice(0, 4);

  return (
    <div className="min-h-screen relative">
      <LeftMenu
        activeMenuItem="sportstest"
        playlist={playlist}
        profile={profile}
      />
      <MobileMenu />

      {/* CSS Animations */}
      <style>{`
        @media (min-width: 0px) {
          .detail-container {
            margin-left: calc(1rem + 30px);
            margin-right: 1rem;
          }
        }
        @media (min-width: 640px) {
          .detail-container {
            margin-left: calc(2rem + 30px);
            margin-right: 2rem;
          }
        }
        @media (min-width: 1024px) {
          .detail-container {
            margin-left: calc(4rem + 30px);
            margin-right: 4rem;
          }
        }
        @media (min-width: 1280px) {
          .detail-container {
            margin-left: calc(5rem + 30px);
            margin-right: 5rem;
          }
        }
        @keyframes livePulseDetail {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.85); }
        }
        .live-pulse-detail {
          animation: livePulseDetail 1.5s ease-in-out infinite;
        }
        @keyframes fadeInDetail {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .detail-fade-in {
          animation: fadeInDetail 0.4s ease-out forwards;
        }
        .detail-fade-in-delay-1 {
          animation: fadeInDetail 0.4s ease-out 0.1s forwards;
          opacity: 0;
        }
        .detail-fade-in-delay-2 {
          animation: fadeInDetail 0.4s ease-out 0.2s forwards;
          opacity: 0;
        }
        @media (prefers-reduced-motion: reduce) {
          .live-pulse-detail { animation: none; }
          .detail-fade-in,
          .detail-fade-in-delay-1,
          .detail-fade-in-delay-2 {
            animation: none;
            opacity: 1;
          }
        }
      `}</style>

      {/* ================================================================ */}
      {/* SPORT BACKGROUND - full-bleed fixed layer                        */}
      {/* ================================================================ */}
      <div className="fixed inset-0 z-0">
        <img
          alt=""
          className="w-full h-full object-cover"
          src={sportBg}
          style={{ opacity: 0.4 }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, transparent 20%, #0d1117 55%)",
          }}
        />
      </div>

      {/* Video Player Modal */}
      {playerOpen && currentStreamUrl && (
        <PlyrVideoPlayer
          isOpen={playerOpen}
          streamUrl={currentStreamUrl}
          title={currentStreamTitle}
          onClose={handleClosePlayer}
        />
      )}

      {/* ================================================================ */}
      {/* MAIN CONTENT                                                     */}
      {/* ================================================================ */}
      <div className="relative z-10 detail-container">
        {/* ============================================================== */}
        {/* HERO HEADER (~30vh)                                            */}
        {/* ============================================================== */}
        <div className="relative pt-[50px] pb-8 min-h-[30vh] flex flex-col justify-center detail-fade-in">
          {/* League badge + name + LIVE indicator */}
          <div className="flex items-center justify-center gap-3 mb-8">
            {leagueLogo && (
              <img
                alt={league}
                className="w-7 h-7 object-contain"
                src={leagueLogo}
              />
            )}
            <span className="text-white/40 text-xs font-bold uppercase tracking-widest">
              {league || sportName}
            </span>
            {isLive && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/30">
                <span className="w-2 h-2 rounded-full bg-red-500 live-pulse-detail" />
                <span className="text-red-400 text-xs font-bold uppercase">
                  Live {gameTime && `\u00B7 ${gameTime}`}
                </span>
              </div>
            )}
            {isFinal && (
              <span className="px-2.5 py-1 rounded-full bg-white/10 text-white/50 text-xs font-bold uppercase">
                Final
              </span>
            )}
          </div>

          {/* Team Matchup */}
          {homeTeam && awayTeam ? (
            <div className="flex items-center justify-center gap-6 md:gap-10 lg:gap-16 mb-6">
              {/* Home Team */}
              <div className="flex flex-col items-center gap-3">
                {homeTeamLogo ? (
                  <img
                    alt={homeTeam}
                    className="w-20 h-20 md:w-28 md:h-28 lg:w-36 lg:h-36 object-contain drop-shadow-xl"
                    src={homeTeamLogo}
                  />
                ) : (
                  <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-3xl font-bold text-white/30">
                      {homeTeam.charAt(0)}
                    </span>
                  </div>
                )}
                <span className="text-base md:text-lg text-white font-semibold text-center max-w-[180px]">
                  {homeTeam}
                </span>
                <FollowButton
                  entity={{ name: homeTeam, logo: homeTeamLogo }}
                  size="sm"
                  type="team"
                  variant="full"
                />
              </div>

              {/* Score */}
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-3 md:gap-4">
                  <span className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white tabular-nums">
                    {homeScore}
                  </span>
                  <span className="text-2xl md:text-3xl font-light text-white/20">
                    -
                  </span>
                  <span className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white tabular-nums">
                    {awayScore}
                  </span>
                </div>
              </div>

              {/* Away Team */}
              <div className="flex flex-col items-center gap-3">
                {awayTeamLogo ? (
                  <img
                    alt={awayTeam}
                    className="w-20 h-20 md:w-28 md:h-28 lg:w-36 lg:h-36 object-contain drop-shadow-xl"
                    src={awayTeamLogo}
                  />
                ) : (
                  <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-3xl font-bold text-white/30">
                      {awayTeam.charAt(0)}
                    </span>
                  </div>
                )}
                <span className="text-base md:text-lg text-white font-semibold text-center max-w-[180px]">
                  {awayTeam}
                </span>
                <FollowButton
                  entity={{ name: awayTeam, logo: awayTeamLogo }}
                  size="sm"
                  type="team"
                  variant="full"
                />
              </div>
            </div>
          ) : (
            <h1 className="text-2xl md:text-4xl font-bold text-white text-center mb-4">
              {event?.strEvent || event?.name || event?.title}
            </h1>
          )}
        </div>

        {/* ============================================================== */}
        {/* TAB BAR                                                        */}
        {/* ============================================================== */}

        {/* Venue / Stadium info — above tabs */}
        {(venue || gameDate) && (
          <div className="flex flex-col items-center mt-[80px] mb-5 detail-fade-in-delay-1">
            {gameDate && (
              <span className="flex items-center gap-2 text-base text-white/60">
                <i className="fas fa-calendar-alt text-white/40" />
                <span className="font-medium">{formatGameDate()}</span>
              </span>
            )}
            {venue && (
              <span className="flex items-center gap-2 text-lg text-white/50 mt-1.5">
                <i className="fas fa-map-marker-alt text-white/30" />
                <span className="font-medium">{venue}</span>
              </span>
            )}
          </div>
        )}

        <div
          className={`flex gap-2 mb-8 ${!(venue || gameDate) ? "mt-[100px]" : "mt-0"} detail-fade-in-delay-1 flex-wrap`}
        >
          {TABS.map((tab, idx) => (
            <button
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${
                activeTab === tab.key
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                  : focusedTab === idx && activeTab !== tab.key
                    ? "bg-white/10 text-white/70 border border-white/30 ring-1 ring-white/20"
                    : "bg-white/[0.06] text-white/40 border border-white/[0.08] hover:bg-white/[0.1] hover:text-white/60"
              }`}
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setFocusedTab(idx);
              }}
            >
              <i className={`fas ${tab.icon} text-sm`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ============================================================== */}
        {/* TAB CONTENT                                                    */}
        {/* ============================================================== */}
        <div className="pb-12 detail-fade-in-delay-2">
          {/* ------------------------------------------------------------ */}
          {/* OVERVIEW TAB                                                  */}
          {/* ------------------------------------------------------------ */}
          {activeTab === "overview" && (
            <div className="space-y-8">
              {/* Channels Preview (top 4) */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <i className="fas fa-satellite-dish text-blue-400/60 text-lg" />
                    <h3 className="text-sm font-bold text-white/40 uppercase tracking-wider">
                      Channels
                    </h3>
                    {channels.length > 0 && (
                      <span className="text-xs text-white/25">
                        {channels.length} available
                      </span>
                    )}
                  </div>
                  {channels.length > 4 && (
                    <button
                      className="text-xs text-blue-400/70 hover:text-blue-400 font-medium transition-colors"
                      onClick={() => {
                        setActiveTab("channels");
                        setFocusedTab(1);
                      }}
                    >
                      View All{" "}
                      <i className="fas fa-chevron-right text-[9px] ml-1" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {topChannels.map((channel, index) => (
                    <div
                      className="bg-white/[0.04] backdrop-blur-md rounded-xl border border-white/[0.08] hover:border-blue-500/30 hover:bg-white/[0.07] transition-all duration-200 cursor-pointer group"
                      key={channel.id || index}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleChannelClick(channel)}
                      onKeyDown={activateOnEnterOrSpace}
                    >
                      <div className="p-4 flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center overflow-hidden flex-shrink-0">
                          {channel.stream_icon ? (
                            <img
                              alt={channel.name}
                              className="w-full h-full object-cover"
                              src={channel.stream_icon}
                              onError={(e) => {
                                e.target.style.display = "none";
                                if (e.target.nextSibling)
                                  e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <div
                            className="w-full h-full items-center justify-center"
                            style={{
                              display: channel.stream_icon ? "none" : "flex",
                            }}
                          >
                            <i className="fas fa-tv text-white/20 text-lg" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-white truncate">
                            {channel.name}
                          </div>
                          {channel.currentProgram && (
                            <div className="text-xs text-white/30 mt-0.5 truncate">
                              {channel.currentProgram.title}
                            </div>
                          )}
                        </div>
                        <div className="w-10 h-10 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 group-hover:border-blue-500 transition-all duration-200">
                          <i className="fas fa-play text-blue-400 text-xs group-hover:text-white transition-colors" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Match Info — compact one-liner */}
              {(() => {
                const infoParts = [];

                if (league) infoParts.push({ label: "League", value: league });
                if (sportName && sportName !== "SPORTS")
                  infoParts.push({ label: "Sport", value: sportName });
                if (venue) infoParts.push({ label: "Venue", value: venue });
                if (gameDate)
                  infoParts.push({ label: "Date", value: formatGameDate() });
                if (season) infoParts.push({ label: "Season", value: season });
                if (referee)
                  infoParts.push({ label: "Referee", value: referee });
                if (attendance)
                  infoParts.push({
                    label: "Attendance",
                    value: Number(attendance).toLocaleString(),
                  });
                if (gameStatus)
                  infoParts.push({
                    label: "Status",
                    value: gameStatus + (gameTime ? ` - ${gameTime}'` : ""),
                  });
                if (infoParts.length === 0) return null;

                return (
                  <div className="flex items-center gap-2 flex-wrap text-sm text-white/40">
                    <i className="fas fa-info-circle text-white/20 text-xs" />
                    {infoParts.map((part, i) => (
                      <span
                        className="flex items-center gap-1"
                        key={part.label}
                      >
                        {i > 0 && (
                          <span className="text-white/15 mx-1">&middot;</span>
                        )}
                        <span className="text-white/30">{part.label}</span>
                        <span className="text-white/60 font-medium">
                          {part.value}
                        </span>
                      </span>
                    ))}
                  </div>
                );
              })()}

              {/* Momentum Bar (LIVE only) */}
              {isLive && (
                <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <i className="fas fa-bolt text-yellow-400/60 text-lg" />
                    <h3 className="text-sm font-bold text-white/40 uppercase tracking-wider">
                      Momentum
                    </h3>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-white/70">
                      {homeTeam}
                    </span>
                    <span className="text-sm font-semibold text-white/70">
                      {awayTeam}
                    </span>
                  </div>
                  <div className="flex h-4 rounded-full overflow-hidden bg-white/[0.06]">
                    <div
                      className="bg-blue-500 transition-all duration-700 rounded-l-full"
                      style={{ width: `${momentum.home}%` }}
                    />
                    <div
                      className="bg-red-500 transition-all duration-700 rounded-r-full"
                      style={{ width: `${momentum.away}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-blue-400 font-medium">
                      {momentum.home}%
                    </span>
                    <span className="text-xs text-red-400 font-medium">
                      {momentum.away}%
                    </span>
                  </div>
                </div>
              )}

              {/* Timeline Preview (last 3 events) */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <i className="fas fa-stream text-purple-400/60 text-lg" />
                    <h3 className="text-sm font-bold text-white/40 uppercase tracking-wider">
                      Timeline
                    </h3>
                  </div>
                  {timeline.length > 3 && (
                    <button
                      className="text-xs text-blue-400/70 hover:text-blue-400 font-medium transition-colors"
                      onClick={() => {
                        setActiveTab("timeline");
                        setFocusedTab(3);
                      }}
                    >
                      View All ({timeline.length}){" "}
                      <i className="fas fa-chevron-right text-[9px] ml-1" />
                    </button>
                  )}
                </div>
                {timeline.length === 0 ? (
                  <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-6 text-center">
                    <i className="fas fa-stream text-2xl text-white/10 mb-2" />
                    <p className="text-white/30 text-sm">No events yet</p>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute left-[19px] top-0 bottom-0 w-px bg-white/[0.08]" />
                    <div className="space-y-1">
                      {timeline
                        .sort((a, b) => (b.time || 0) - (a.time || 0))
                        .slice(0, 3)
                        .map((evt, idx) => {
                          const isHome =
                            evt.team?.name === homeTeam ||
                            evt.team?.name === "HOME";

                          return (
                            <div
                              className="relative flex items-start gap-4 py-2 pl-0"
                              key={idx}
                            >
                              <div className="relative z-10 flex-shrink-0">
                                {getTimelineIcon(evt)}
                              </div>
                              <div className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-semibold text-white/80">
                                    {evt.detail || evt.type}
                                  </span>
                                  <span className="text-xs text-white/30 font-mono">
                                    {evt.time ? `${evt.time}'` : ""}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                      isHome
                                        ? "bg-blue-500/15 text-blue-400"
                                        : "bg-red-500/15 text-red-400"
                                    }`}
                                  >
                                    {evt.team?.name ||
                                      (isHome ? "Home" : "Away")}
                                  </span>
                                  <span className="text-sm text-white/60">
                                    {evt.player}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>

              {/* Stats Preview */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <i className="fas fa-chart-bar text-green-400/60 text-lg" />
                    <h3 className="text-sm font-bold text-white/40 uppercase tracking-wider">
                      Match Stats
                    </h3>
                  </div>
                  <button
                    className="text-xs text-blue-400/70 hover:text-blue-400 font-medium transition-colors"
                    onClick={() => {
                      setActiveTab("stats");
                      setFocusedTab(2);
                    }}
                  >
                    Full Stats{" "}
                    <i className="fas fa-chevron-right text-[9px] ml-1" />
                  </button>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-6">
                  <MatchStats compact event={event} />
                </div>
              </div>

              {/* H2H Preview */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <i className="fas fa-exchange-alt text-orange-400/60 text-lg" />
                    <h3 className="text-sm font-bold text-white/40 uppercase tracking-wider">
                      Head to Head
                    </h3>
                  </div>
                  <button
                    className="text-xs text-blue-400/70 hover:text-blue-400 font-medium transition-colors"
                    onClick={() => {
                      setActiveTab("h2h");
                      setFocusedTab(4);
                    }}
                  >
                    Full H2H{" "}
                    <i className="fas fa-chevron-right text-[9px] ml-1" />
                  </button>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-6">
                  <HeadToHead
                    compact
                    awayTeam={awayTeam}
                    homeTeam={homeTeam}
                    sport={event?.sport}
                  />
                </div>
              </div>

              {/* Highlights */}
              {highlights.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <i className="fas fa-video text-green-400/60 text-lg" />
                    <h3 className="text-sm font-bold text-white/40 uppercase tracking-wider">
                      Highlights
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {highlights.slice(0, 3).map((hl) => {
                      const ytId = getYouTubeId(hl.video);

                      return (
                        <div
                          className="rounded-xl bg-white/[0.04] border border-white/[0.08] overflow-hidden"
                          key={hl.id}
                        >
                          {ytId ? (
                            <div className="aspect-video">
                              <iframe
                                allowFullScreen
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                className="w-full h-full"
                                src={`https://www.youtube.com/embed/${ytId}`}
                                title={hl.title}
                              />
                            </div>
                          ) : hl.thumb ? (
                            <div className="aspect-video relative">
                              <img
                                alt={hl.title}
                                className="w-full h-full object-cover"
                                src={hl.thumb}
                              />
                              <a
                                className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors"
                                href={hl.video}
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                <i className="fas fa-play-circle text-white text-4xl" />
                              </a>
                            </div>
                          ) : null}
                          <div className="p-4">
                            <p className="text-sm font-medium text-white/80 truncate">
                              {hl.title}
                            </p>
                            {hl.date && (
                              <p className="text-xs text-white/30 mt-1">
                                {hl.date}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ------------------------------------------------------------ */}
          {/* CHANNELS TAB                                                 */}
          {/* ------------------------------------------------------------ */}
          {activeTab === "channels" && (
            <div>
              <div className="flex items-center gap-3 mb-5">
                <i className="fas fa-satellite-dish text-blue-400/60 text-lg" />
                <h2 className="text-xl font-bold text-white">All Channels</h2>
                {!channelsLoading && channels.length > 0 && (
                  <span className="text-sm text-white/30">
                    {channels.length} channel
                    {channels.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {channelsLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {[...Array(6)].map((_, i) => (
                    <div
                      className="h-28 rounded-xl bg-white/[0.04] border border-white/[0.06] animate-pulse"
                      key={i}
                    />
                  ))}
                </div>
              ) : channels.length === 0 ? (
                <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-8 text-center">
                  <i className="fas fa-satellite-dish text-3xl text-white/10 mb-3" />
                  <p className="text-white/30 text-sm">
                    No channels currently broadcasting this event
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {channels.map((channel, index) => (
                    <div
                      className="bg-white/[0.04] backdrop-blur-md rounded-xl border border-white/[0.08] hover:border-blue-500/30 hover:bg-white/[0.07] transition-all duration-200 cursor-pointer group"
                      key={channel.id || index}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleChannelClick(channel)}
                      onKeyDown={activateOnEnterOrSpace}
                    >
                      <div className="p-5 flex items-center gap-4">
                        <div className="w-16 h-16 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center overflow-hidden flex-shrink-0">
                          {channel.stream_icon ? (
                            <img
                              alt={channel.name}
                              className="w-full h-full object-cover"
                              src={channel.stream_icon}
                              onError={(e) => {
                                e.target.style.display = "none";
                                if (e.target.nextSibling)
                                  e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <div
                            className="w-full h-full items-center justify-center"
                            style={{
                              display: channel.stream_icon ? "none" : "flex",
                            }}
                          >
                            <i className="fas fa-tv text-white/20 text-xl" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-base font-semibold text-white truncate">
                            {channel.name}
                          </div>
                          {channel.currentProgram && (
                            <div className="text-sm text-white/30 mt-0.5 truncate">
                              {channel.currentProgram.title}
                            </div>
                          )}
                          {channel.currentProgram && (
                            <div className="text-xs text-white/20 mt-0.5">
                              {channel.currentProgram.start} -{" "}
                              {channel.currentProgram.stop}
                            </div>
                          )}
                        </div>
                        <div className="w-12 h-12 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 group-hover:border-blue-500 transition-all duration-200">
                          <i className="fas fa-play text-blue-400 text-sm group-hover:text-white transition-colors" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ------------------------------------------------------------ */}
          {/* STATS TAB                                                    */}
          {/* ------------------------------------------------------------ */}
          {activeTab === "stats" && <MatchStats event={event} />}

          {/* ------------------------------------------------------------ */}
          {/* TIMELINE TAB                                                 */}
          {/* ------------------------------------------------------------ */}
          {activeTab === "timeline" && (
            <div>
              <div className="flex items-center gap-3 mb-5">
                <i className="fas fa-stream text-purple-400/60 text-lg" />
                <h2 className="text-xl font-bold text-white">Match Timeline</h2>
              </div>

              {timelineLoading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div
                      className="h-16 rounded-xl bg-white/[0.04] border border-white/[0.06] animate-pulse"
                      key={i}
                    />
                  ))}
                </div>
              ) : timeline.length === 0 ? (
                <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-8 text-center">
                  <i className="fas fa-stream text-3xl text-white/10 mb-3" />
                  <p className="text-white/30 text-sm">
                    No timeline events available
                  </p>
                </div>
              ) : (
                <div className="relative">
                  {/* Vertical line */}
                  <div className="absolute left-[19px] top-0 bottom-0 w-px bg-white/[0.08]" />

                  <div className="space-y-1">
                    {timeline
                      .sort((a, b) => (a.time || 0) - (b.time || 0))
                      .map((evt, idx) => {
                        const isHome =
                          evt.team?.name === homeTeam ||
                          evt.team?.name === "HOME";

                        return (
                          <div
                            className="relative flex items-start gap-4 py-3 pl-0"
                            key={idx}
                          >
                            {/* Icon */}
                            <div className="relative z-10 flex-shrink-0">
                              {getTimelineIcon(evt)}
                            </div>

                            {/* Content */}
                            <div className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-semibold text-white/80">
                                  {evt.detail || evt.type}
                                </span>
                                <span className="text-xs text-white/30 font-mono">
                                  {evt.time ? `${evt.time}'` : ""}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                    isHome
                                      ? "bg-blue-500/15 text-blue-400"
                                      : "bg-red-500/15 text-red-400"
                                  }`}
                                >
                                  {evt.team?.name || (isHome ? "Home" : "Away")}
                                </span>
                                <span className="text-sm text-white/60">
                                  {evt.player}
                                </span>
                                {evt.assist && (
                                  <span className="text-xs text-white/30">
                                    (assist: {evt.assist})
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ------------------------------------------------------------ */}
          {/* H2H TAB                                                      */}
          {/* ------------------------------------------------------------ */}
          {activeTab === "h2h" && (
            <HeadToHead
              awayTeam={awayTeam}
              homeTeam={homeTeam}
              sport={event?.sport}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WebPlayerSportsTestEventDetail;
